"use client";

import { Box, IconButton, TextField, Typography } from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { Trans } from "@lingui/macro";
import { useProductStore } from "@/components/context/product-store";
import React from "react";
import { useShallow } from "zustand/react/shallow";

interface QuantitySelectorProps {
  minimum: number;
  maximum: number;
  label?: boolean;
}

const QuantitySelector = ({ minimum, maximum, label }: QuantitySelectorProps) => {
  const { qty, setQty } = useProductStore(
    useShallow((state) => ({
      qty: state.qty,
      setQty: state.setQty,
    })),
  );

  const handleChangeQty = (action: "add" | "subtract") => {
    let currentQty: number;
    if (typeof qty === "number") {
      currentQty = qty;
    } else {
      currentQty = qty === "" ? minimum : parseInt(qty, 10);
    }

    if (action === "add") {
      if (maximum > 0 && currentQty < maximum) {
        setQty(Math.min(maximum, currentQty + 1));
      } else if (maximum === 0) {
        setQty(currentQty + 1);
      }
    } else if (action === "subtract") {
      setQty(Math.max(minimum, currentQty - 1));
    }
  };

  const handleQtyInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (inputValue.trim() === "") {
      setQty("");
      return;
    }

    const parsed = parseInt(inputValue, 10);
    if (!isNaN(parsed)) {
      setQty(parsed);
    } else {
      setQty(minimum);
    }
  };

  const handleBlur = () => {
    let numericQty: number;
    if (qty === "") {
      numericQty = minimum;
    } else if (typeof qty === "number") {
      numericQty = qty;
    } else {
      numericQty = parseInt(qty, 10);
    }

    if (isNaN(numericQty) || numericQty < minimum) {
      setQty(minimum);
      return;
    }

    if (maximum > 0 && numericQty > maximum) {
      setQty(maximum);
      return;
    }
    setQty(numericQty);
  };

  return (
    <Box sx={{ pt: "30px" }} data-testid="quantity-section">
      {label && (
        <Typography sx={{ fontWeight: 600, mb: 1, fontSize: "16px" }}>
          <Trans>Quantity</Trans>
        </Typography>
      )}
      <Box sx={{ display: "flex", gap: 1 }}>
        <IconButton
          aria-label="quantity-btn-subtract"
          data-testid="quantity-btn-subtract"
          disableRipple
          onClick={() => handleChangeQty("subtract")}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "100%",
            bgcolor: "rgb(246, 246, 247)",
            width: "40px",
            height: "40px",
            minWidth: "auto",
          }}
        >
          <RemoveIcon />
        </IconButton>
        <TextField
          data-testid="quantity-input"
          onBlur={handleBlur}
          onChange={handleQtyInputChange}
          variant="outlined"
          type="number"
          value={qty}
          sx={{
            borderRadius: "10px",
            width: "60px",
            height: "40px",
            "& .MuiOutlinedInput-input": {
              textAlign: "center",
              p: 0,
              "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                WebkitAppearance: "none",
                margin: 0,
              },
              "&[type=number]": {
                MozAppearance: "textfield",
              },
            },
          }}
        />
        <IconButton
          aria-label="quantity-btn-add"
          data-testid="quantity-btn-add"
          disableRipple
          onClick={() => handleChangeQty("add")}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "100%",
            bgcolor: "rgb(246, 246, 247)",
            width: "40px",
            height: "40px",
            minWidth: "auto",
          }}
        >
          <AddIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default React.memo(QuantitySelector);
